<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="classificationForm">
      <!-- Domain & SubDomain -->
      <v-row v-for="(item, index) in domainsList" :key="'domain-' + index" align="center">
        <v-col cols="12" md="5">
          <label>{{ $t('scientific-domain') }}</label>
          <p class="label-info fixed-label-height">
            The branch of science, scientific discipline <br />
            that is related to the Equipment.
          </p>
          <v-autocomplete
            v-model="item.domain"
            :items="domains"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="$t('please-select-domain')"
            hide-details
            clearable
          >
          </v-autocomplete>
        </v-col>
        <v-col cols="10" md="5">
          <label>{{ $t('scientific-subdomain') }}</label>
          <p class="label-info fixed-label-height">
            {{ $t('equipment-scientific-subdomain-message') }}
          </p>
          <v-autocomplete
            v-model="item.subdomain"
            :items="filteredSubdomains[index]"
            :item-value="'id'"
            :item-text="'subDomain'"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="$t('please-select-subdomain')"
            hide-details
            clearable
          ></v-autocomplete>
        </v-col>
        <v-col cols="2" class="pt-15 mt-5" v-if="filteredSubdomains.length > 1">
          <v-btn fab small text @click="removeDomain(index)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Add more Domains & Subdomains -->
      <v-row class="mb-4 mt-0 pt-0" v-if="hasSelectedDomain">
        <v-col class="mt-5">
          <v-btn
            @click="addDomain"
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-scientific-domain') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Category and Subcategory -->
      <v-row
        v-for="(itemCategory, index) in categoriesList"
        :key="'category-' + index"
        align="center"
        class="mt-1"
      >
        <v-col cols="12" md="5">
          <label>{{ $t('category') }}</label>
          <p class="label-info fixed-label-height">
            Since this is an equipment, the instruments & Equipment <br />
            value is preselected.
          </p>
          <v-autocomplete
            v-model="itemCategory.category"
            :items="categories"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="$t('please-select-category')"
            hide-details
            clearable
            disabled
          >
          </v-autocomplete>
        </v-col>
        <v-col cols="10" md="5">
          <label>{{ $t('subcategory') }}</label>
          <p class="label-info fixed-label-height">{{ $t('equipment-subcategory') }}</p>
          <v-autocomplete
            v-model="itemCategory.subcategory"
            :items="filteredSubcategories[index]"
            :item-value="'id'"
            :item-text="'subCategory'"
            outlined
            :rules="requiredRule()"
            class="required"
            :placeholder="$t('please-select-subcategory')"
            hide-details
            clearable
          ></v-autocomplete>
        </v-col>
        <v-col cols="2" class="pt-15 mt-5" v-if="filteredSubcategories.length > 1">
          <v-btn fab small text @click="removeCategory(index)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Add more Categories & Subcategories -->
      <v-row class="mb-4 mt-0 pt-0" v-if="hasSelectedCategory">
        <v-col class="mt-5">
          <v-btn
            @click="addCategory"
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-category') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>Access Mode</label>
                    <p class="label-info fixed-label-height">
                      Eligibility/criteria for granting access to the Resource to users
                      (excellence-based, free-conditionally, free etc.)
                    </p>
                    <v-select
                      v-model="optionalEquipment.accessMode"
                      :items="accessModes"
                      clearable
                      :placeholder="'Select Access Modes'"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      outlined
                      multiple
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>Access Type</label>
                    <p class="label-info fixed-label-height">
                      The way a user can access the Equipment <br />(Remote, Physical, Virtual,
                      etc.)
                    </p>
                    <v-select
                      v-model="optionalEquipment.accessType"
                      :items="accessTypes"
                      clearable
                      :placeholder="'Select Access Types'"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      outlined
                      multiple
                    >
                    </v-select>
                  </v-col>
                </v-row>
                <v-row class="mt-0">
                  <v-col cols="12" sm="12" md="6">
                    <label>Target Users</label>
                    <p class="label-info">
                      Specify Target Users of the Resource (Researchers, Students, Businesses etc.)
                    </p>
                    <v-select
                      v-model="optionalEquipment.target"
                      :items="targets"
                      clearable
                      :placeholder="'Select Access Modes'"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      outlined
                      multiple
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('tags') }}</label>
                    <p class="label-info">{{ $t('tags-message') }}</p>
                    <v-combobox
                      v-model="optionalEquipment.tags"
                      clearable
                      :placeholder="'Select Tags'"
                      multiple
                      persistent-hint
                      small-chips
                      return-object
                      outlined
                      hint="Type to create a new tag"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Type and press <span class="text-bold">enter</span> to create a new
                              tag
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          :close="true"
                          @click:close="removeChip(item)"
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updateEquipmentClassificationInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import base from '@/services/base-service.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'EquipmentClassificationInfo',
  mixins: [util, base],
  components: { AlertUnsavedChanges },
  data: () => ({
    domainsList: [{ domain: '', subdomain: '' }],
    initialDomainList: [],
    initialCategoriesList: [],
    categoriesList: [{ category: 'Instruments & Equipment', subcategory: '' }],
    domains: [],
    subdomains: [],
    categories: [],
    subCategories: [],
    unsavedChanges: false,
    dataLoaded: false,
    accessModes: [],
    accessTypes: [],
    targets: [],
    optionalEquipment: {
      accessMode: [],
      accessType: [],
      target: [],
      tags: [],
    },
    initialAccessModes: [],
    initialAccessTypes: [],
    initialTargets: [],
    initialTags: [],
    initialStatus: {},
    status: false,
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  created() {
    this.getScientificDomains().then(res => {
      this.domains = res.data.content;
    });

    this.getScientificSubdomains().then(res => {
      this.subdomains = res.data.content;
    });
    this.getCategories().then(res => {
      this.categories = res.data.content;
    });
    this.getSubCategories().then(res => {
      this.subCategories = res.data.content;
    });
    this.getAccessModes().then(res => {
      this.accessModes = res.data.content;
    });
    this.getAccessTypes().then(res => {
      this.accessTypes = res.data.content;
    });
    this.getTargets().then(res => {
      this.targets = res.data.content;
    });
  },
  mounted() {
    if (this.edit) {
      this.getEquipmentIdClassificationInfo();
    }
  },
  watch: {
    domainsList: {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialDomainList)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    categoriesList: {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialCategoriesList)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalEquipment.accessMode': {
      deep: true,
      handler(newAccessMode) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newAccessMode) !== JSON.stringify(this.initialAccessModes)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalEquipment.accessType': {
      deep: true,
      handler(newAccessType) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newAccessType) !== JSON.stringify(this.initialAccessTypes)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalEquipment.target': {
      deep: true,
      handler(newAccessTarget) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newAccessTarget) !== JSON.stringify(this.initialTargets)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalEquipment.tags': {
      deep: true,
      handler(newAccessTag) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newAccessTag) !== JSON.stringify(this.initialTags)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
  },
  computed: {
    hasSelectedDomain() {
      return this.domainsList.some(item => item.domain);
    },
    hasSelectedCategory() {
      return this.categoriesList.some(item => item.category);
    },
    filteredSubcategories() {
      return this.categoriesList.map(item =>
        this.subCategories.filter(sub => sub.category === item.category),
      );
    },
    filteredSubdomains() {
      return this.domainsList.map(item =>
        this.subdomains.filter(sub => sub.domain === item.domain),
      );
    },
  },
  methods: {
    addDomain() {
      this.domainsList.push({ domain: '', subdomain: '' });
    },
    addCategory() {
      this.categoriesList.push({ category: 'Instruments & Equipment', subcategory: '' });
    },
    removeDomain(index) {
      if (this.domainsList.length > 1) {
        this.domainsList.splice(index, 1);
        this.filteredSubdomains.splice(index, 1);
      }
    },
    removeCategory(index) {
      if (this.categoriesList.length > 1) {
        this.categoriesList.splice(index, 1);
        this.filteredSubcategories.splice(index, 1);
      }
    },
    removeChip(itemText) {
      this.optionalEquipment.tags.forEach(obj => {
        if (obj === itemText) {
          this.optionalEquipment.tags.splice(this.optionalEquipment.tags.indexOf(obj), 1);
        }
      });
      this.optionalEquipment.tags = [...this.optionalEquipment.tags];
    },
    next() {
      const isFormValid = this.$refs.classificationForm.validate();
      if (isFormValid) {
        const scientificsIds = this.domainsList.map(domain => domain.subdomain);
        const categoriesIds = this.categoriesList.map(category => category.subcategory);

        this.$emit('tab', 3);
        this.$emit('equipmentClassificationInfo', {
          scientificsIds,
          categoriesIds,
        });
      }
    },
    back() {
      this.$emit('tab', 1);
    },
    getEquipmentIdClassificationInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/equipment/${id}`)
        .then(response => {
          console.log('Get Equipment Classification Info', response.data);
          const equipmentData = response.data;

          this.status = response.data.published;

          this.initialStatus = {
            published: response.data.published,
          };
          this.$emit('status', this.initialStatus.published);

          if (equipmentData.scientifics) {
            this.domainsList = equipmentData.scientifics.map(domain => ({
              domain: domain.domain,
              subdomain: domain.id,
            }));
            this.initialDomainList = JSON.parse(JSON.stringify(this.domainsList));
          }

          if (equipmentData.categories) {
            this.categoriesList = equipmentData.categories.map(category => ({
              category: category.category,
              subcategory: category.id,
            }));
            this.initialCategoriesList = JSON.parse(JSON.stringify(this.categoriesList));
          }

          if (equipmentData.accessModes) {
            this.optionalEquipment.accessMode = equipmentData.accessModes.map(mode => ({
              id: mode.id,
              name: mode.name,
            }));

            this.initialAccessModes = JSON.parse(JSON.stringify(this.optionalEquipment.accessMode));
          }

          if (equipmentData.accessTypes) {
            this.optionalEquipment.accessType = equipmentData.accessTypes.map(mode => ({
              id: mode.id,
              name: mode.name,
            }));

            this.initialAccessTypes = JSON.parse(JSON.stringify(this.optionalEquipment.accessType));
          }

          if (equipmentData.targets) {
            this.optionalEquipment.target = equipmentData.targets.map(mode => ({
              id: mode.id,
              name: mode.name,
            }));

            this.initialTargets = JSON.parse(JSON.stringify(this.optionalEquipment.target));
          }

          if (equipmentData.tags) {
            this.optionalEquipment.tags = equipmentData.tags;

            this.initialTags = JSON.parse(JSON.stringify(this.optionalEquipment.tags));
          }

          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching equipment data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    updateEquipmentClassificationInfo() {
      const id = this.$route.params.id;

      const scientificsIds = this.domainsList.map(domain => {
        const subdomain = this.subdomains.find(
          sub => sub.subDomain === domain.subdomain && sub.domain === domain.domain,
        );
        return subdomain ? subdomain.id : domain.subdomain;
      });

      const categoriesIds = this.categoriesList.map(category => {
        const subcategory = this.subCategories.find(
          sub => sub.subCategory === category.subcategory && sub.category === category.category,
        );
        return subcategory ? subcategory.id : category.subcategory;
      });

      const accessModes = this.optionalEquipment.accessMode.map(mode => ({
        id: mode.id,
        name: mode.name,
      }));

      const accessTypes = this.optionalEquipment.accessType.map(mode => ({
        id: mode.id,
        name: mode.name,
      }));

      const targets = this.optionalEquipment.target.map(mode => ({
        id: mode.id,
        name: mode.name,
      }));

      const tags = this.optionalEquipment.tags.map(tag => tag);

      const equipmentUpdateData = {
        id: id,
        published: this.published,
        scientificsIds: scientificsIds,
        categoriesIds: categoriesIds,
        accessModes: accessModes,
        accessTypes: accessTypes,
        targets: targets,
        tags: tags,
      };

      this.axios
        .put(`/api/v1/equipment/classification-info`, equipmentUpdateData)
        .then(response => {
          console.log('Equipment classification updated successfully:', response.data);
          EventBus.$emit('snackbar', {
            text: 'Equipment Updated Successfully',
          });

          this.unsavedChanges = false;
          this.initialStatus = this.status;
          this.$emit('updatedChanges', true);

          this.getEquipmentIdClassificationInfo();
        })
        .catch(error => {
          console.error('Error updating equipment info:', error);
        });
    },
    cancel() {
      this.getEquipmentIdClassificationInfo();
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 3em;
}
</style>
